import { useCallback, useEffect, useState } from 'react'
import type { TablesInsert, TablesUpdate } from '../../../supabase/functions/_shared/database.types'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { CalendarEntry } from '../types/database.tables'
import { formatDateForComparison } from '../utils/dateUtils'

export type CalendarEntryCreate = TablesInsert<'calendar_entries'>
export type CalendarEntryUpdate = TablesUpdate<'calendar_entries'>

export interface UseCalendarEntriesReturn {
  entries: CalendarEntry[]
  isLoading: boolean
  error: string | null
  loadEntries: (startDate: Date, endDate: Date) => Promise<void>
  createEntry: (data: CalendarEntryCreate) => Promise<CalendarEntry>
  updateEntry: (id: string, data: CalendarEntryUpdate) => Promise<CalendarEntry>
  deleteEntry: (id: string) => Promise<void>
  getEntriesForDate: (date: Date) => CalendarEntry[]
}

export const useCalendarEntries = (): UseCalendarEntriesReturn => {
  const { user } = useAuth()
  const [entries, setEntries] = useState<CalendarEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Function to load calendar entries for a date range
  const loadEntries = useCallback(
    async (startDate: Date, endDate: Date): Promise<void> => {
      if (!user) {
        setEntries([])
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const startDateString = formatDateForComparison(startDate)
        const endDateString = formatDateForComparison(endDate)

        const { data, error: dbError } = await supabase
          .from('calendar_entries')
          .select('*')
          .eq('user_id', user.id)
          .gte('date', startDateString)
          .lte('date', endDateString)
          .order('date', { ascending: true })
          .order('start_time', { ascending: true })

        if (dbError) {
          console.error('Error loading calendar entries:', dbError)
          throw dbError
        }

        setEntries(data || [])
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load calendar entries'
        console.error('Failed to load calendar entries:', err)
        setError(errorMessage)
        setEntries([])
      } finally {
        setIsLoading(false)
      }
    },
    [user]
  )

  const createEntry = useCallback(
    async (data: CalendarEntryCreate): Promise<CalendarEntry> => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      try {
        const newEntry = {
          ...data,
          user_id: user.id,
        } as CalendarEntry

        const { data: createdData, error: dbError } = await supabase
          .from('calendar_entries')
          .insert(newEntry)
          .select()
          .single()

        if (dbError) {
          console.error('Error creating calendar entry:', dbError)
          throw dbError
        }

        console.log('Successfully created calendar entry:', createdData)
        setEntries((prev) => [newEntry, ...prev])
        return createdData
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create calendar entry'
        console.error('Failed to create calendar entry:', err)
        setError(errorMessage)
        throw err
      }
    },
    [user]
  )

  // Function to update existing calendar entry
  const updateEntry = useCallback(
    async (id: string, updates: CalendarEntryUpdate): Promise<CalendarEntry> => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      try {
        const { data, error: dbError } = await supabase
          .from('calendar_entries')
          .update(updates)
          .eq('id', id)
          .eq('user_id', user.id)
          .select()
          .single()

        if (dbError) {
          console.error('Error updating calendar entry:', dbError)
          throw dbError
        }

        console.log('Successfully updated calendar entry:', data)

        // Update local state
        setEntries((prev) => prev.map((entry) => (entry.id === id ? data : entry)))
        return data
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update calendar entry'
        console.error('Failed to update calendar entry:', err)
        setError(errorMessage)
        throw err
      }
    },
    [user]
  )

  // Function to delete calendar entry
  const deleteEntry = useCallback(
    async (id: string): Promise<void> => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      try {
        const { error: dbError } = await supabase.from('calendar_entries').delete().eq('id', id).eq('user_id', user.id)

        if (dbError) {
          console.error('Error deleting calendar entry:', dbError)
          throw dbError
        }

        console.log('Successfully deleted calendar entry:', id)
        setEntries((prev) => prev.filter((entry) => entry.id !== id))
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete calendar entry'
        console.error('Failed to delete calendar entry:', err)
        setError(errorMessage)
        throw err
      }
    },
    [user]
  )

  // Function to get entries for a specific date
  const getEntriesForDate = useCallback(
    (date: Date): CalendarEntry[] => {
      const dateString = formatDateForComparison(date)
      return entries.filter((entry) => entry.date === dateString)
    },
    [entries]
  )

  // Clear entries when user changes
  useEffect(() => {
    if (!user) {
      setEntries([])
      setError(null)
    }
  }, [user])

  return {
    entries,
    isLoading,
    error,
    loadEntries,
    createEntry,
    updateEntry,
    deleteEntry,
    getEntriesForDate,
  }
}
